import { useCustomSnackbar } from '@procureai/ui-mui';
import { RefObject, useMemo, useRef, useState } from 'react';

import { StepConfig, WizardContext, WizardRef } from '../../WizardState';

export interface UseWizardProps<T> {
  initialState: T;
  steps: StepConfig[];
  onClose: () => void;
}

interface UseWizardResult<T> {
  currentStep: number;
  steps: StepConfig[];
  contentRef: RefObject<WizardRef | null>;
  contextValue: WizardContext<T>;
}

export const useWizard = <T>({ initialState, steps, onClose }: UseWizardProps<T>): UseWizardResult<T> => {
  const [state, setState] = useState<T>(initialState);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const { onError } = useCustomSnackbar();

  const contentRef = useRef<WizardRef>(null);

  const contextValue = useMemo(
    () => ({
      state,
      loading,
      nextStep: () => setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1)),
      previousStep: () => setCurrentStep((prev) => Math.max(prev - 1, 0)),
      reset: () => {
        setCurrentStep(0);
        setState(initialState);
      },
      updateState: (newState: Partial<T>) => setState((prevState) => ({ ...prevState, ...newState })),
      setLoading,
      triggerFormSubmission: async () => {
        if (contentRef.current) {
          return await contentRef.current.onSubmit();
        }
        return false;
      },
      onClose,
      onError: (error: any) => {
        setLoading(false);
        onError(error);
      },
    }),
    [state, loading, onClose, steps.length, initialState, onError],
  );

  return { currentStep, steps, contentRef, contextValue };
};
